import { useId, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  RangeInputs,
  type RangeInputsProps,
  type RangeValue,
} from "../RangeInputs";
import { rangesEqual } from "../RangeInputs/rangeValues";

export type RangePreset = { id: string; label: string; range: RangeValue };
export type RangeSelectorProps = RangeInputsProps & {
  presets: readonly RangePreset[];
};

export const RangeSelector = ({
  presets,
  className,
  ...inputProps
}: RangeSelectorProps) => {
  const id = useId();

  const [selectedPresetId, setSelectedPresetId] = useState<string>();

  const selectedPreset = presets.find(
    (preset) => preset.id === selectedPresetId,
  );

  const selection =
    selectedPreset && rangesEqual(selectedPreset.range, inputProps.value)
      ? selectedPreset.id
      : "custom";

  if (!presets.length) {
    return <RangeInputs {...inputProps} className={className} />;
  }

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2 text-sm text-zinc-700",
        className,
      )}
    >
      {presets.map((preset) => (
        <label
          key={preset.id}
          className="flex cursor-pointer items-center gap-2"
        >
          <input
            className="h-4 w-4 accent-purple-600"
            type="radio"
            name={id}
            disabled={inputProps.disabled}
            checked={selection === preset.id}
            onChange={() => {
              setSelectedPresetId(preset.id);
              inputProps.onChange(preset.range);
            }}
          />
          {preset.label}
        </label>
      ))}
      <label className="flex cursor-pointer items-center gap-2">
        <input
          className="h-4 w-4 accent-purple-600"
          type="radio"
          name={id}
          disabled={inputProps.disabled}
          checked={selection === "custom"}
          onChange={() => {
            setSelectedPresetId(undefined);
            inputProps.onChange(undefined);
          }}
        />
        Custom range
      </label>
      <div className="pl-6">
        <RangeInputs
          key={selection}
          {...inputProps}
          disabled={inputProps.disabled || selection !== "custom"}
          onChange={(value) => {
            setSelectedPresetId(undefined);
            inputProps.onChange(value);
          }}
        />
      </div>
    </div>
  );
};
